<template>
  <!-- Card for Image Upload -->

  <div class="image-card list-item">
    <div v-if="showHeader" class="image-card__header">
      <div v-if="headerType === 'alert'" class="image-card__header-alert">
        <div class="image-card__header-alert-banner">
          <div class="image-card__header-alert-icon--success">
            <i class="fas fa-check-circle mr-2"></i>
          </div>
          <p class="image-card__header-alert-text">{{ alertHeader }}</p>
        </div>
        <div v-if="showUploadDate" class="image-card__header-text">
          Uploaded: {{ uploadDate }}
        </div>
      </div>

      <div v-else>
        <span class="image-card__number" :class="imageNumberClass">{{
          image.imageNum
        }}</span>
        <span v-if="showUploadDate" class="image-card__header-text"
          >Uploaded: {{ uploadDate }}
        </span>
      </div>
    </div>
    <div class="image-card__body">
      <div v-if="image.docStatus === 'ok'" class="image-card__image">
        <!-- - image here - -->
        <img
          v-if="imageThumbURL !== 'error' && imageThumbURL !== ''"
          :src="imageThumbURL"
          alt=""
        />
        <div v-else>
          <b-overlay
            :show="fetchingImgUrl"
            rounded
            opacity="0.2"
            spinner-small
            spinner-variant="white"
            class="d-inline-block"
          >
            <b-button
              ref="button"
              :disabled="fetchingImgUrl"
              variant="primary"
              size="sm"
              @click="setThumbImageURL(image)"
              >Fetch Image</b-button
            >
          </b-overlay>
          <p
            v-if="fetchImageErrorResult"
            class="text-error text-smaller-1 my-2"
          >
            {{ fetchImageErrorResult }}
          </p>
        </div>
      </div>

      <div
        v-if="uploadInProgress && image.docStatus !== 'ok'"
        class="image-card__row image-card__row--red"
      >
        <p>Please wait...</p>
        <p>{{ imageFilename }}</p>
      </div>

      <div v-if="!uploadInProgress && image.docStatus !== 'ok'">
        <div class="image-card__row image-card__row--red">
          <p>
            This upload may not have processed properly. Click Refresh to see if
            the image and text load.
          </p>
        </div>
        <div class="image-card__row image-card__row--light-gray">
          <button class="image-card__btn" @click="rerenderView">
            <i class="fas fa-sync-alt"></i>Refresh
          </button>
        </div>
        <div class="image-card__row image-card__row--red">
          <p>
            After refreshing, if you still see this message, you should delete
            this upload to free up a slot here in your UPLOADS bin.
          </p>
          <p class="mb-0">Name of image file you tried to upload:</p>
          <p>{{ imageFilename }}</p>
        </div>
      </div>

      <div v-if="image.docStatus === 'ok'">
        <p class="image-card__dog-name" :class="dogNameClass">
          {{ image.dogName }}
        </p>
        <p class="image-card__breed">{{ getBreed }}</p>
        <!-- <p class="image-card__user-name">{{ image.fName }} {{ image.lName }}</p>
        <p class="image-card__user-location">{{ image.location }}</p> -->
        <h6 class="image-card__body-heading">Caption</h6>
        <p class="image-card__caption">
          {{ image.caption }}
        </p>

        <div v-if="showRejReason">
          <h6 class="image-card__body-heading">Reason Unusable</h6>
          <p class="image-card__caption">
            {{ image.rejReason }}
          </p>
        </div>
      </div>
    </div>
    <div class="image-card__footer">
      <div v-if="showButtonPanel" class="image-card__btn-panel">
        <router-link
          v-if="image.docStatus === 'ok'"
          :to="'/replace-image/' + image.id"
          class="image-card__btn"
          ><i class="far fa-image"></i>Replace Image</router-link
        >
        <router-link
          v-if="image.docStatus === 'ok'"
          :to="'/edit-image-text/' + image.id"
          class="image-card__btn"
          ><i class="far fa-edit"></i>Edit Text</router-link
        >
        <button
          v-if="!uploadInProgress"
          class="image-card__btn image-card__btn--red"
          @click="showConfirmDelete(image)"
        >
          <i class="far fa-trash-alt"></i>Delete...
        </button>
      </div>
      <div v-if="showProductsPanel" class="products">
        <div class="image-card__row image-card__row--green">
          <p>
            This image of {{ image.dogName }} appears in the following
            publication{{ s }}:
          </p>
        </div>
        <div class="image-card__row image-card__row--light-green">
          <ul class="image-card__list">
            <li v-for="project in image.projects" :key="project.creTime">
              {{ project.title }}
              <a
                class="image-card__link image-card__link--indented"
                :href="project.url"
                target="_blank"
                >Get it on {{ project.platform }}</a
              >
            </li>
            <!-- <li>
              The Yellow Lab Lover's Productivity Journal, Vol. 1
              <a
                class="image-card__link image-card__link--indented"
                href="http://amazon.com"
                target="_blank"
                >Get it on Amazon</a
              >
            </li>
            <li>
              2021 Wall Calendar, Labrador Retrievers, Vol. 1
              <a
                class="image-card__link image-card__link--indented"
                href="http://lulu.com"
                target="_blank"
                >Get it on Lulu</a
              >
            </li> -->
          </ul>
        </div>
        <div class="image-card__row image-card__row--green">
          <p v-if="image.breedId === 'mixed'">
            A portion of the proceeds from the sales of the above product{{ s }}
            will be donated to rescue organizations that work with mixed-breed
            dogs.
          </p>
          <p v-else>
            A portion of the proceeds from the sales of the above product{{
              s
            }}
            will be donated to {{ getBreed }} rescue organizations.
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// import { Upload } from '@/models/Upload';

import { mapGetters } from 'vuex';

// Get associated thumbnail file name for a given image filename
// and size:  g1.jpg -->> 150px thumb filename = g1_150x150.jpg
function thumbFilename(sourceFilename, size) {
  const fileNameSizeSegment = `_${size}x${size}`;
  const re = /(\.\w+$)/;
  return sourceFilename.replace(re, fileNameSizeSegment + '$1');
}
var imageTypeColor = {
  pending: 'orange',
  selected: 'blue',
  published: 'green',
  rejected: 'red',
};

export default {
  name: 'CardImage',

  // props: image: image object where properties are img attrs like dogName, breed, fName, lName, etc.
  // props: [
  //   'type',
  //   'headerType',
  //   'image',
  //   'showHeader',
  //   'showButtonPanel',
  //   'showProductsPanel',
  //   'showRejReason',
  // ],
  props: {
    type: String,
    headerType: String,
    alertHeader: String, // e.g. alert title text
    image: Object,
    showHeader: Boolean,
    showUploadDate: {
      type: Boolean,
      default: true,
    },
    showButtonPanel: Boolean,
    showProductsPanel: Boolean,
    showRejReason: Boolean,
  },
  data: function () {
    return {
      fetchingImgUrl: false,
      // deleteConfirmed: false,

      fetchImageErrorResult: '',
      showFetchImageResult: false,
    };
  },
  computed: {
    ...mapGetters('auth', ['authUser']),
    ...mapGetters(['uploadInProgress']),

    // pluralize noun (publication, product, etc.) with 's'... or not
    s: function () {
      return this.image.projects.length > 1 ? 's' : '';
    },
    classColor: function () {
      return imageTypeColor[this.type];
    },
    imageNumberClass: function () {
      var classObj = {};
      classObj['image-card__number--' + this.classColor] = true;
      return classObj;
    },
    dogNameClass: function () {
      var classObj = {};
      classObj['image-card__dog-name--' + this.classColor] = true;
      return classObj;
    },
    uploadDate: function () {
      return new Date(this.image.creTime).toDateString();
    },
    imageFilename: function () {
      return this.image.imageFilename === undefined
        ? 'file-name-not-found'
        : this.image.imageFilename;
    },
    imageThumbURL: function () {
      return this.image.imageThumbURL;
    },
    getBreed: function () {
      // Should ALWAYS have breedId, but maybe during upload the associated breed never resolved (??)... so, to not "break"
      // THIS layout, if breed is falsey (i.e. enpty string), get breed from store using breedId
      return this.image.breed || this.$store.state.breeds[this.image.breedId];
    },
  },
  methods: {
    rerenderView: function () {
      this.$parent.$parent.rerenderView();
    },

    // Have been times when the thumbnail image URL could not be fetched during initial image upload (i.e. it timed out, leaving
    // the URL to be 'error'). The thumbnail image was created and the upload doc HAS the url, but not until AFTER the tracker
    // doc was created. So, tracker doc has 'error' for value of thumbnail image url.
    // If that's the case, a button appears where the image should be in the card. Clicking that button runs this method to get the
    // thumb url and save it to upload doc thus triggering an update (since setting serverTimestamp in that doc -- without that, no change
    // to doc possibly if in fact the thumbnail image url IS there) to the tracker doc, saving it there.
    setThumbImageURL: async function (image) {
      var mainErrorMessagePrefix = 'Fetch Image failed. ';
      var mainErrorMessage = `Looks like there was a problem with either the original
            upload or a "Change Image" upload (if you tried that at some point). You should DELETE this upload, then try uploading again.`;

      this.fetchImageErrorResult = ''; // Clear any previous error result text
      this.fetchingImgUrl = true; // Show button overlay with spinner
      var imageUploadRef = this.$fapi.getDocRefById('imageUploads', image.id);
      var uploadDoc = await imageUploadRef.get();
      if (uploadDoc.exists) {
        var thumbNailPath = 'dogimgs/' + this.authUser.uid + '/thumbs/';
        var thumbnailImgUrl = '';
        try {
          thumbnailImgUrl = await this.$fapi.getImageUrl(
            thumbNailPath + thumbFilename(image.imageFilename, 200)
          );
          if (thumbnailImgUrl === 'error') {
            throw new Error(this.$fapi.error);
          }
        } catch (err) {
          if (/not exist/.test(err.message)) {
            this.fetchImageErrorResult =
              mainErrorMessagePrefix +
              'Image does not exist. ' +
              mainErrorMessage +
              ' Error Code: NE';
          } else {
            this.fetchImageErrorResult =
              mainErrorMessagePrefix + err.message + ' ' + mainErrorMessage;
          }
          // console.error(err.message);
          // console.log(thumbnailImgUrl, this.$fapi.error);
          this.fetchingImgUrl = false; // Hide button overlay
          return;
        }

        try {
          // This update of the imageUpload doc will throw error due to fb security rules: Existing imageUpload doc
          // MUST have both image URLs populated; if either one is empty, it's a signal that
          // either image upload (orig or replacement) did not go to completion. If THAT'S the
          // case, user is notified to delete this upload.
          await imageUploadRef.update({
            imageThumbURL: thumbnailImgUrl,
            updatedAt: this.$fapi.firebase.firestore.FieldValue.serverTimestamp(),
          });
        } catch (err) {
          if (/permission/.test(err)) {
            this.fetchImageErrorResult =
              mainErrorMessagePrefix + mainErrorMessage + ' Error Code: PE';
          } else {
            this.fetchImageErrorResult = mainErrorMessagePrefix + err;
          }
          console.log('xxx');
        }

        // Show the image, replacing the 'Fetch Image' button
        // this.image.imageThumbURL = thumbnailImgUrl;
      }
      this.fetchingImgUrl = false; // Hide button overlay
    },

    showConfirmDelete: function (imageObj) {
      this.$emit('delete-clicked', imageObj);
    },
    showConfirmDelete_v1: async function (uploadId) {
      this.deleteConfirmed = false;
      var isDeleteConfirmed = await this.$bvModal.msgBoxConfirm(
        'Delete this upload? ' + uploadId,
        {
          title: 'Please Confirm',
          size: 'md',
          buttonSize: 'sm',
          okVariant: 'primary',
          okTitle: 'Yes, delete',
          cancelTitle: 'No',
          // footerClass: 'p-2',
          hideHeaderClose: true,
          centered: true,
        }
      );
      // console.log('delete confirmed?', isDeleteConfirmed);
      if (isDeleteConfirmed) {
        this.$emit('confirm-delete-clicked', uploadId);
        // this.deleteImage(uploadId);
        // console.log('deleted id:', uploadId);
      }
    },
    //deleteImage: function (id) {
    // this.$store.dispatch('setDeletedImageId', id);
    // },
  },
};
</script>

<style>
</style>

